import { Capacitor } from "@capacitor/core";
import React from "react";
import { match } from "ts-pattern";
import { API, APIConfig, LoginData } from "../../../../API";
import ConfirmInformationStage from "./ConfirmInformationStage";
import RegisterStage from "./RegisterStage";

interface Props {
  apiConfig: APIConfig;
  loginData: LoginData;
  phonenumber: string | null;
  onValidConfirmDetails: React.ComponentProps<typeof ConfirmInformationStage>["onValid"];
  onValidRegistration: React.ComponentProps<typeof RegisterStage>["onSuccess"];
  onVersionOutdated: () => void;
}

const LoggedInStage = (props: Props) => {
  const ctrl = React.useMemo(() => {
    const caregiver = props.loginData.payload.userLoginDetails.caregiver;

    if (caregiver === undefined) {
      return { type: "Register" as const };
    }

    const api = new API(
      props.apiConfig,
      {
        accessJWT: props.loginData.tokens.accessJWT,
        refreshJWT: props.loginData.tokens.refreshJWT,
        exp: props.loginData.payload.exp,
        caregiverId: caregiver.id,
      },
      Capacitor.getPlatform()
    );

    return { type: "ConfirmDetails" as const, api, caregiver };
  }, [
    props.apiConfig,
    props.loginData.payload.exp,
    props.loginData.payload.userLoginDetails.caregiver,
    props.loginData.tokens.accessJWT,
    props.loginData.tokens.refreshJWT,
  ]);

  return match(ctrl)
    .with({ type: "Register" }, () => (
      <RegisterStage
        loginData={props.loginData}
        onVersionOutdated={props.onVersionOutdated}
        onSuccess={props.onValidRegistration}
        phonenumber={props.phonenumber}
        apiConfig={props.apiConfig}
      />
    ))
    .with({ type: "ConfirmDetails" }, ({ api, caregiver }) => (
      <ConfirmInformationStage
        api={api}
        caregiver={caregiver}
        onValid={props.onValidConfirmDetails}
      />
    ))
    .exhaustive();
};

export default LoggedInStage;
